import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import apiCoreAdapter from './apiCoreAdapter';

/**
 * Tarifa de la psicóloga (`psychologist.fee_type` de api-core) tal como la devuelve
 * GET /api/psychologist/profile: 'range' | 'usd_v1' | 'usd_v2' | 'ars'.
 *
 *  - `string`    → tarifa conocida.
 *  - `null`      → la psicóloga no tiene tarifa cargada.
 *  - `undefined` → no se pudo saber (todavía cargando, el fetch falló, o el backend es anterior
 *    al campo). El que consume decide el fallback; no se cachea para reintentar la próxima vez.
 *
 * Existe para que la vista de Liquidación dependa de la TARIFA y nunca del ranking: una psicóloga
 * range sigue siendo range aunque su categoría cambie (NZ → O → …). Cachea por psychologistId, mismo
 * patrón que useContactChannel: varios consumidores no multiplican el fetch.
 */
export interface UsePsychologistFeeTypeResult {
  feeType: string | null | undefined;
  loading: boolean;
}

const feeTypeCache = new Map<string, string | null>();
const feeTypePending = new Map<string, Promise<string | null | undefined>>();

/** Solo para tests: limpia la cache de módulo. */
export function __resetFeeTypeCacheForTests(): void {
  feeTypeCache.clear();
  feeTypePending.clear();
}

export function usePsychologistFeeType(): UsePsychologistFeeTypeResult {
  const { psychologist } = usePsychologistContext();
  const { psychologistId: authPsychologistId } = useAuth();
  const psychologistId = authPsychologistId || psychologist?.id || '';

  const cached = psychologistId ? feeTypeCache.get(psychologistId) : undefined;
  const [feeType, setFeeType] = useState<string | null | undefined>(cached);
  const [loading, setLoading] = useState<boolean>(!!psychologistId && !feeTypeCache.has(psychologistId));

  useEffect(() => {
    let cancelled = false;
    if (!psychologistId) {
      setLoading(false);
      return () => { cancelled = true; };
    }

    if (feeTypeCache.has(psychologistId)) {
      setFeeType(feeTypeCache.get(psychologistId));
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    let pending = feeTypePending.get(psychologistId);
    if (!pending) {
      pending = apiCoreAdapter
        .psychologistProfile()
        // Campo ausente (backend anterior) → undefined: no es "sin tarifa" (null).
        .then((profile) => profile.fee_type)
        .catch(() => undefined);
      feeTypePending.set(psychologistId, pending);
    }
    pending
      .then((value) => {
        if (value !== undefined) feeTypeCache.set(psychologistId, value);
        if (!cancelled) setFeeType(value);
      })
      .finally(() => {
        feeTypePending.delete(psychologistId);
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [psychologistId]);

  return { feeType, loading };
}
